import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '@/components/ui/toaster';
import { newsletterApi } from '@/lib/api/newsletter';

interface SubscribeFormProps {
  variant?: 'inline' | 'large';
  onSuccess?: () => void;
}

export function SubscribeForm({
  variant = 'inline',
  onSuccess,
}: SubscribeFormProps) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) return;

    if (!consent) {
      toast.error('Consenso richiesto', {
        description: 'Devi accettare la privacy policy per iscriverti.',
      });
      return;
    }

    setIsLoading(true);
    try {
      await newsletterApi.subscribe(email);
      toast.success('Controlla la tua email', {
        description:
          'Conferma l’iscrizione con il collegamento ricevuto. Se sei già iscritto, continuerai a ricevere Segnali AI.',
      });
      setEmail('');
      setConsent(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Errore', {
        description:
          error instanceof Error ? error.message : 'Iscrizione fallita',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const ConsentCheckbox = () => (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        cursor: 'pointer',
        marginTop: 14,
      }}
    >
      <input
        type="checkbox"
        checked={consent}
        onChange={(e) => setConsent(e.target.checked)}
        style={{
          width: 18,
          height: 18,
          marginTop: 2,
          accentColor: '#3B82F6',
          cursor: 'pointer',
        }}
      />
      <span style={{ fontSize: 12, color: '#5A5A72', lineHeight: 1.5 }}>
        Acconsento al trattamento dei miei dati personali secondo la
        <Link
          to="/privacy-policy"
          style={{ color: '#3B82F6', textDecoration: 'underline' }}
        >
          Privacy Policy
        </Link>
        . Newsletter settimanale gratuita, cancellazione in qualsiasi momento.
      </span>
    </label>
  );

  if (variant === 'large') {
    return (
      <form
        onSubmit={handleSubmit}
        style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}
      >
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <span
              style={{
                position: 'absolute',
                left: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#8A8AA0',
                fontSize: 16,
              }}
            >
              ✉️
            </span>
            <input
              type="email"
              aria-label="La tua email"
              placeholder="La tua email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '14px 18px 14px 42px',
                borderRadius: 12,
                border: '1px solid rgba(26,26,46,0.12)',
                background: '#F8F8FA',
                color: '#1A1A2E',
                fontSize: 15,
                outline: 'none',
                transition: 'all 0.3s ease',
              }}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '14px 28px',
              borderRadius: 12,
              border: 'none',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: '#0A0A0B',
              fontWeight: 700,
              fontSize: 15,
              cursor: isLoading ? 'wait' : 'pointer',
              transition: 'all 0.3s ease',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? '...' : 'Iscriviti gratis'}
          </button>
        </div>
        <ConsentCheckbox />
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 400 }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <input
          type="email"
          aria-label="La tua email"
          placeholder="La tua email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
          style={{
            flex: 1,
            maxWidth: 280,
            padding: '12px 16px',
            borderRadius: 10,
            border: '1px solid rgba(26,26,46,0.12)',
            background: '#F8F8FA',
            color: '#1A1A2E',
            fontSize: 14,
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={isLoading}
          style={{
            padding: '12px 20px',
            borderRadius: 10,
            border: 'none',
            background: '#3B82F6',
            color: 'white',
            fontWeight: 600,
            fontSize: 14,
            cursor: isLoading ? 'wait' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
          }}
        >
          {isLoading ? '...' : 'Iscriviti'}
        </button>
      </div>
      <ConsentCheckbox />
    </form>
  );
}
